/**
 * Google Ads conversion tracking + attribution helpers.
 *
 * gtag.js is loaded in /public/index.html with the real conversion ID
 * `AW-17909065009`. Conversion labels are read from build-time env vars
 * so marketing can rotate them in Railway without a code change.
 *
 *   REACT_APP_GADS_LEAD_LABEL      → fires after OTP-verified lead submit
 *   REACT_APP_GADS_PURCHASE_LABEL  → fires after Razorpay payment success
 *   REACT_APP_GADS_CALL_LABEL      → fires on every tel: click (via index.html)
 *   REACT_APP_GADS_BOOK_NOW_LABEL  → optional soft conversion on book-now click
 *
 * All event functions are SAFE TO CALL even before gtag is loaded — they
 * push to `window.dataLayer` defensively. If a label is missing the call
 * silently no-ops so the LP never breaks while marketing is still setting
 * up conversion actions in the Google Ads UI.
 */

const ADS_CONVERSION_ID =
  process.env.REACT_APP_GADS_CONVERSION_ID || "AW-17909065009";

/**
 * Per-event conversion labels. Marketing creates each conversion action
 * in Google Ads UI (Tools → Conversions → New conversion action), copies
 * the label suffix (the part after the slash in `AW-xxx/yyy`), and sets
 * it as an env var on Railway. Empty default = silent no-op.
 */
const CONVERSION_LABELS = {
  lead_submitted:   process.env.REACT_APP_GADS_LEAD_LABEL || "",     // PRIMARY — verified OTP lead
  purchase:         process.env.REACT_APP_GADS_PURCHASE_LABEL || "", // PRIMARY — Razorpay paid (ROAS)
  pricing_revealed: process.env.REACT_APP_GADS_PRICING_LABEL || "",  // Secondary — quote shown (soft)
  book_now_clicked: process.env.REACT_APP_GADS_BOOK_NOW_LABEL || "", // Secondary — book-now intent
  // NOTE: call-click + WhatsApp-click conversions are fired by single
  // GLOBAL delegated listeners in public/index.html (reading
  // REACT_APP_GADS_CALL_LABEL / REACT_APP_GADS_WHATSAPP_LABEL) so every
  // tel:/wa.me CTA across the LP fires exactly once, in real time.
  call_clicked:     process.env.REACT_APP_GADS_CALL_LABEL || "",     // legacy key — now fired via index.html
};

/* ------------------------------------------------------------------ */
/* Internal helpers                                                    */
/* ------------------------------------------------------------------ */

function pushDataLayer(payload) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

function gtagSafe(...args) {
  if (typeof window === "undefined") return;
  if (typeof window.gtag === "function") {
    window.gtag(...args);
  } else {
    pushDataLayer(args);
  }
}

/* ------------------------------------------------------------------ */
/* Events                                                              */
/* ------------------------------------------------------------------ */

/**
 * Standard event push — fires to GA4 / GTM if connected.
 */
export function trackEvent(name, params = {}) {
  gtagSafe("event", name, params);
}

/**
 * Google Ads conversion. No-ops cleanly if the label env var isn't set.
 *
 * @param {keyof typeof CONVERSION_LABELS} kind
 * @param {{ value?:number, currency?:string, transaction_id?:string }} [opts]
 */
export function trackConversion(kind, opts = {}) {
  const label = CONVERSION_LABELS[kind];
  if (label === undefined) {
    console.warn(`[gtag] unknown conversion kind: ${kind}`);
    return;
  }
  if (!label) {
    // Env var not configured yet — push a dataLayer breadcrumb for QA
    // but skip the Google Ads send.
    pushDataLayer(["conversion_pending_label", { kind, ...opts }]);
    return;
  }
  gtagSafe("event", "conversion", {
    send_to: `${ADS_CONVERSION_ID}/${label}`,
    value: opts.value,
    currency: opts.currency || "INR",
    transaction_id: opts.transaction_id,
  });
  // Mirror to GA4 as a regular event for analytics joins.
  trackEvent(kind, { value: opts.value, currency: opts.currency || "INR" });
}

/**
 * Enhanced Conversions — hashed user info improves attribution ~15%
 * post-iOS-14. Called once per lead with the verified phone number.
 */
export function setEnhancedConversion({ email, phone }) {
  if (!email && !phone) return;
  gtagSafe("set", "user_data", {
    email: email || undefined,
    phone_number: phone || undefined,
  });
}

/* ------------------------------------------------------------------ */
/* Attribution                                                          */
/* ------------------------------------------------------------------ */

/**
 * Returns the full attribution bag captured by the IIFE in index.html
 * from sessionStorage key `wd_attr`. Includes:
 *   gclid, gbraid, wbraid, gad_source,
 *   utm_source, utm_medium, utm_campaign, utm_term, utm_content,
 *   landing_page, referrer
 *
 * Safe to spread directly into the lead-submit POST body — every field
 * is Optional on the CRM side and missing keys are simply absent.
 */
export function getAttribution() {
  if (typeof window === "undefined") return {};
  try {
    const raw = sessionStorage.getItem("wd_attr");
    if (raw) return JSON.parse(raw);
  } catch (_) { /* sessionStorage disabled — silent */ }
  return {};
}

/**
 * Back-compat shim. Older callers still expect a UTM-shaped object
 * with `source`/`campaign` etc. New code should prefer getAttribution()
 * which returns the full flat set.
 */
export function captureUtm() {
  const a = getAttribution();
  return {
    source:   a.utm_source,
    medium:   a.utm_medium,
    campaign: a.utm_campaign,
    term:     a.utm_term,
    content:  a.utm_content,
    gclid:    a.gclid,
    referrer: a.referrer,
    utm_source:   a.utm_source,
    utm_campaign: a.utm_campaign,
  };
}

/**
 * Generic GA4 event tracker — single chokepoint for all funnel events.
 * Spec-compliant naming: callers should use exact event names + parameter
 * keys per the GA4 funnel exploration definition. Safe to call before
 * gtag has finished loading — falls back to dataLayer push.
 *
 * @param {string} name  GA4 event name (e.g. "scroll_50", "cta_click")
 * @param {Object} params Flat parameter bag. Avoid nested objects.
 */
export function track(name, params = {}) {
  gtagSafe("event", name, params);
}

/* ------------------------------------------------------------------ */
/* Funnel events — exact spec from product/marketing                  */
/* ------------------------------------------------------------------ */

const SESS_KEY_PREFIX = "wd_funnel:";

function fireOnce(sessionKey, fn) {
  if (typeof window === "undefined") return;
  try {
    const k = SESS_KEY_PREFIX + sessionKey;
    if (sessionStorage.getItem(k)) return;
    sessionStorage.setItem(k, "1");
  } catch (_) { /* sessionStorage disabled — fire anyway */ }
  fn();
}

/** 1. scroll_50 — once per session when user scrolls past 50% page height */
export function trackScroll50() {
  fireOnce("scroll_50", () => track("scroll_50", {
    page_location: typeof window !== "undefined" ? window.location.href : "",
    page_path: typeof window !== "undefined" ? window.location.pathname : "",
  }));
}

/** 2. cta_click — any call-to-action button */
export function trackCtaClick({ label, location }) {
  track("cta_click", {
    cta_label: label || "unknown",
    cta_location: location || "unknown",
    page_path: typeof window !== "undefined" ? window.location.pathname : "",
  });
}

/** 3. form_start — once per form per session on first focus */
export function trackFormStart({ formId, firstField }) {
  fireOnce(`form_start:${formId}`, () => track("form_start", {
    form_id: formId,
    first_field: firstField,
  }));
}

/** 4. field_abandon — field focused then blurred empty (debounced upstream) */
export function trackFieldAbandon({ formId, fieldName }) {
  track("field_abandon", { form_id: formId, field_name: fieldName });
}

/** 5. whatsapp_intent — every WhatsApp icon/button click */
export function trackWhatsappIntent({ location }) {
  track("whatsapp_intent", {
    cta_location: location || "unknown",
    page_path: typeof window !== "undefined" ? window.location.pathname : "",
  });
}

/** 6. otp_sent — backend 2xx on send-otp */
export function trackOtpSent({ formId, mobileCountry = "IN" }) {
  track("otp_sent", { form_id: formId, mobile_country: mobileCountry });
}

/** 6b. otp_send_failed — backend error on send-otp */
export function trackOtpSendFailed({ formId, errorType }) {
  track("otp_send_failed", { form_id: formId, error_type: errorType || "unknown" });
}

/** 7. otp_verified — backend 2xx on verify-otp */
export function trackOtpVerified({ formId }) {
  track("otp_verified", { form_id: formId });
}

const tracking = {
  trackEvent,
  trackConversion,
  setEnhancedConversion,
  captureUtm,
  getAttribution,
  track,
  trackScroll50,
  trackCtaClick,
  trackFormStart,
  trackFieldAbandon,
  trackWhatsappIntent,
  trackOtpSent,
  trackOtpSendFailed,
  trackOtpVerified,
};
export default tracking;
